import { last } from "lodash-es";

import { endOfDay, lastWeekDay, plusDays, startOfDay, startOfWeek } from "@/utils/date";

import { useActivityStore } from "../activity";
import { useSessionStore } from "../session";

export interface DateItem {
  id: string;
  fromDate: Date;
  toDate: Date;
}

export interface DateFilter {
  id: "now" | "workday" | "iteration" | "pi" | "planning" | "week" | "24_hours" | "custom";
  name: [string, string];
  fromDate: () => Date;
  toDate: () => Date;
  show: () => boolean;
}

export const dateFilterValues: DateFilter[] = [
  {
    id: "now",
    name: [/*$t*/ "boardDiff.now", /*$t*/ "boardDiff.now"],
    fromDate: () => new Date(),
    toDate: () => new Date(),
    show: () => useActivityStore().tab === "visualize",
  },
  {
    id: "24_hours",
    name: [/*$t*/ "boardDiff.last24Hours", /*$t*/ "boardDiff.ago24Hours"],
    fromDate: () => plusDays(new Date(), -1),
    toDate: () => new Date(),
    show: () => true,
  },
  {
    id: "workday",
    name: [/*$t*/ "boardDiff.lastWorkday", /*$t*/ "boardDiff.lastWorkdayStart"],
    fromDate: () => startOfDay(lastWeekDay(new Date())),
    toDate: () => new Date(),
    show: () => true,
  },
  {
    id: "week",
    name: [/*$t*/ "boardDiff.thisWeek", /*$t*/ "boardDiff.thisWeekStart"],
    fromDate: () => startOfDay(startOfWeek(new Date())),
    toDate: () => new Date(),
    show: () => ![1, 2].includes(new Date().getDay()), // the filter isn't useful on monday/tuesday
  },
  {
    id: "iteration",
    name: [/*$t*/ "boardDiff.thisIteration", /*$t*/ "boardDiff.thisIterationStart"],
    fromDate: () => useSessionStore().currentIteration!.start,
    toDate: () => endOfDay(plusDays(useSessionStore().currentIteration!.end, -1)),
    show: () => !!useSessionStore().currentIteration, // show during the PI
  },
  {
    id: "pi",
    name: [/*$t*/ "boardDiff.thisPi", /*$t*/ "boardDiff.thisPiStart"],
    fromDate: () => useSessionStore().iterations[0].start,
    toDate: () => endOfDay(plusDays(last(useSessionStore().iterations)!.end, -1)),
    show: () => useSessionStore().sessionHasStarted, // show during the PI
  },
  {
    id: "planning",
    name: [/*$t*/ "boardDiff.planningDates", /*$t*/ "boardDiff.planningEnd"],
    fromDate: () => startOfDay(useSessionStore().current.planningStart!),
    toDate: () => endOfDay(useSessionStore().current.planningEnd!),
    show: () =>
      !useSessionStore().sessionHasStarted && // show during planning, if dates provided
      !!useSessionStore().current.planningStart &&
      !!useSessionStore().current.planningEnd,
  },
];

export function defaultDate(): DateItem {
  const filter = dateFilterValues.find((f) => f.show())!;
  return { id: filter.id, fromDate: filter.fromDate(), toDate: filter.toDate() };
}
